import { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../utility/axios';
import { useTableChanges } from './useWebSocket';

const CONTACTS_MAILBOX_TABLES = [
    'messages',
    'documents',
    'message_drafts',
    'documents_drafts',
    'message_receivers',
    'files',
    'document',
    'document_drafts',
    'message_draft_files',
    'documenso_files',
    'documenso_files_drafts',
    'attachment_comments',
    'attachment_draft_comments',
    'attachment_documenso_draft_comments'
];

export const useContactsMailbox = (rid = null) => {
    const [inboxData, setInboxData] = useState({
        threaded_messages: [],
        documents: [],
    });
    const [loading, setLoading] = useState(true);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [requiresVerification, setRequiresVerification] = useState(false);
    const [receiverEmail, setReceiverEmail] = useState(null);
    const [isFreeAccount, setIsFreeAccount] = useState(false);

    const fetchInbox = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const params = rid ? { rid } : {};
            const response = await axios.get('/api/contacts-mailbox/inbox/', { params });
            setInboxData({
                threaded_messages: response.data.threaded_messages || [],
                documents: response.data.documents || [],
            });
            setDataLoaded(true);
            setRequiresVerification(false);
            setReceiverEmail(response.data.receiver_email || null);
            setIsFreeAccount(response.data.is_free_account || false);
            return response.data;
        } catch (err) {
            const status = err.response?.status;
            const data = err.response?.data;
            if (status === 401 && data?.requires_verification) {
                setRequiresVerification(true);
            }
            setInboxData({ threaded_messages: [], documents: [] });
            setDataLoaded(true);
            if (!(status === 401 && data?.requires_verification)) {
                throw err;
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, [rid]);

    const debounceTimeoutRef = useRef(null);
    const debouncedRefresh = useCallback(() => {
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = setTimeout(() => fetchInbox(true), 300);
    }, [fetchInbox]);

    const handleTableChange = useCallback((change) => {
        if (!CONTACTS_MAILBOX_TABLES.includes(change.table)) return;
        debouncedRefresh();
    }, [debouncedRefresh]);

    useTableChanges(CONTACTS_MAILBOX_TABLES, handleTableChange, true);

    const refresh = useCallback(async () => {
        return fetchInbox();
    }, [fetchInbox]);

    useEffect(() => {
        fetchInbox().catch(() => {});
        return () => {
            if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        };
    }, [fetchInbox]);

    return {
        inboxData,
        loading,
        dataLoaded,
        requiresVerification,
        receiverEmail,
        isFreeAccount,
        refresh,
        fetchInbox,
    };
};

export default useContactsMailbox;
